import json2csv from "json2csv";

// The API returns applicable rules in priority order (merchant before default).
// Customer-specific pricing is represented by an empty/missing rules array.
export const getPricingBands = (merchant) => {
  const min = Number(merchant.limit.min);
  const max = Number(merchant.limit.max);
  const base = Number(merchant.sellPercentage);
  const rules = merchant.adjustmentRules || [];
  const rateAt = (value) => {
    const rule = rules.find(
      (rule) =>
        value >= Number(rule.startValue) && value < Number(rule.endValue)
    );
    return Number((base + (rule ? Number(rule.adjustment) : 0)).toFixed(10));
  };
  const boundaries = [
    ...new Set([
      min,
      max,
      ...rules
        .flatMap((rule) => [Number(rule.startValue), Number(rule.endValue)])
        .filter((value) => value > min && value < max),
    ]),
  ].sort((a, b) => a - b);
  const bands = [];
  const append = (band) => {
    const previous = bands[bands.length - 1];
    if (previous && previous.percentage === band.percentage) {
      previous.max = band.max;
      previous.maxInclusive = band.maxInclusive;
    } else {
      bands.push(band);
    }
  };
  boundaries.slice(0, -1).forEach((start, index) =>
    append({
      min: start,
      max: boundaries[index + 1],
      maxInclusive: false,
      percentage: rateAt(start),
    })
  );
  // A rule ending at the merchant limit does not apply at that exact balance.
  append({ min: max, max, maxInclusive: true, percentage: rateAt(max) });
  return bands;
};

const money = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 10,
  }).format(value);

export const formatBandBalance = (band) =>
  band.min === band.max
    ? money(band.min)
    : `${money(band.min)} – ${money(
        band.maxInclusive ? band.max : Number((band.max - 0.01).toFixed(10))
      )}`;

export const formatMerchantRate = (merchant) => {
  const rates = getPricingBands(merchant).map((band) => band.percentage);
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  return min === max
    ? `${min}% of card balance.`
    : `${min}–${max}%, depending on card balance.`;
};

export const getRateRows = (merchants) =>
  merchants.flatMap((merchant) =>
    getPricingBands(merchant).map((band) => ({
      ...merchant,
      band,
      sellPercentage: band.percentage,
    }))
  );

export const RATE_DISCLOSURE = "Rates are subject to change.";

export const createRatesCsv = (merchants, { retrievedAt } = {}) =>
  json2csv({
    preserveNewLinesInValues: true,
    newLine: "\r\n",
    data: getRateRows(
      [...merchants].sort((a, b) => a.name.localeCompare(b.name))
    ),
    fields: [
      { label: "Brand ID", value: "id" },
      { label: "Brand", value: "name" },
      {
        label: "Card Balance",
        value: (row) => formatBandBalance(row.band).replace(/[$,]/g, ""),
      },
      { label: "Payout (%)", value: "band.percentage" },
      {
        label: "Submission Method",
        value: (row) =>
          row.submissionMethod === "online" ? "Online" : "Mail-in",
      },
      {
        label: "Rates Retrieved (UTC)",
        value: () => (retrievedAt ? new Date(retrievedAt).toISOString() : ""),
      },
      { label: "Disclosure", value: () => RATE_DISCLOSURE },
    ],
  });
